<script setup lang="ts">
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import {computed, ref, watch} from "vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import {useExtensionStore} from "@/stores/extension.store";
import GroupingBranding from "@/core/interfaces/laboratory/GroupingBranding";
import {groupingsApi} from "@/container";
import GroupingHeaderSection from "@/grouping/components/GroupingHeaderSection.vue";
import GroupingBrandingSliderSection from "@/grouping/components/GroupingBrandingSliderSection.vue";
import GroupingExtensionsSection from "@/grouping/components/GroupingExtensionsSection.vue";
import ContestsSection from "@/contest/components/ContestsSection.vue";
import useGroupingContests from "@/contest/composables/useGroupingContests";

const {environment} = storeToRefs(useSystemStore());
const {extensions} = storeToRefs(useExtensionStore());

const contestFilter = ref<"upcoming" | "ended" | null>(null);
const {data: contests, isLoading} = useGroupingContests(contestFilter);
const branding = ref<GroupingBranding>();
const isLoadingBranding = ref(false);

/**
 * Retrieve the current healthcenter grouping from system store.
 */
const grouping = computed(() => {
    return environment.value?.entity?.grouping;
});

watch(() => environment.value?.entity?.grouping, (_grouping) => {
    if (_grouping) {
        isLoadingBranding.value = true;
        branding.value = undefined;

        groupingsApi().branding(_grouping.id)
            .then(data => {
                branding.value = data;
            })
            .catch(() => {
                branding.value = undefined;
            })
            .finally(() => {
                isLoadingBranding.value = false;
            });
    }
}, {immediate: true});

/**
 * Extract extensions provided by the current healthcenter grouping.
 */
const groupingExtensions = computed(() => {
    if (!grouping.value?.id) {
        return [];
    }

    return extensions.value?.filter(extension => {
        return extension.organization.entity_type === 'grouping'
            && extension.organization.entity_id === grouping.value?.id;
    }) ?? [];
});

/**
 * Check if there are extensions to display.
 */
const hasExtensions = computed(() => {
    return groupingExtensions.value.length > 0;
});
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
    <BaseContainer class="py-8 px-6 max-w-7xl mx-auto">
      <div v-if="!grouping" class="text-center py-20">
        <div class="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
          <div class="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            Aucun groupement
          </h2>
          <p class="text-gray-600">
            Vous ne faites partie d'aucun groupement pour le moment.
          </p>
        </div>
      </div>

      <div v-else class="space-y-12">
        <GroupingHeaderSection :grouping="grouping" :branding="branding" :is-loading="isLoadingBranding" />

        <GroupingExtensionsSection v-if="hasExtensions" :extensions="groupingExtensions" />

        <ContestsSection v-model:status="contestFilter" :contests="contests ?? []" :loading="isLoading" />

        <GroupingExtensionsSection v-if="!hasExtensions" :extensions="groupingExtensions" />

        <GroupingBrandingSliderSection :is-loading="isLoadingBranding" :branding="branding" />

        <div class="text-center py-8">
          <div class="inline-flex items-center space-x-2 text-gray-400">
            <div class="w-2 h-2 bg-blue-300 rounded-full animate-pulse" />
            <div class="w-2 h-2 bg-indigo-300 rounded-full animate-pulse" style="animation-delay: 0.2s" />
            <div class="w-2 h-2 bg-purple-300 rounded-full animate-pulse" style="animation-delay: 0.4s" />
          </div>
        </div>
      </div>
    </BaseContainer>
  </div>
</template>

<style scoped>
</style>
